import type { PayloadAction } from '@reduxjs/toolkit';
import type { Action } from 'redux-actions';
import { handleActions } from 'redux-actions';
import { DialogProcessorActions } from 'app/actions/dialogprocessor/dialogProcessorActions.ts';

import type { BlockDto, ProcessResultDto } from '@/api-gen/api.schemas';
import type { DialogProcessorStoredBlock, IDProcessState } from 'app/models/dialogs/dialogProcess.ts';
import logger from 'app/utils/logger.ts';

const initialState: IDProcessState = {
  sessionId: null,
  blocks: { lastBlock: null, currentBlock: null },
  isFallback: false
};

const buildStoredBlock = (
  block: BlockDto | undefined,
  rest: Partial<Omit<ProcessResultDto, 'lastBlock' | 'prevBlock'>> = {}
): DialogProcessorStoredBlock | null => {
  if (!block) {
    return null;
  }

  return {
    ...block,
    ...rest
  };
};

export const dialogProcessorReducer = handleActions<
  IDProcessState,
  string | ProcessResultDto | any
>(
  {
    [DialogProcessorActions.Type.SET_STORE_RESPONSE_DATA]: (
      state,
      action: Action<ProcessResultDto>
    ): IDProcessState => {
      const { lastBlock, prevBlock, ...restProps } = action.payload;
      // Validate sessionId in response
      if (!restProps.sessionId) {
        logger.warn('Received null sessionId in SET_STORE_RESPONSE_DATA', {
          currentSessionId: state.sessionId,
          lastBlockId: lastBlock?.id
        });
        // Keep existing sessionId instead of setting null
        restProps.sessionId = state.sessionId;
      } else if (restProps.sessionId !== state.sessionId) {
        logger.info('DialogProcessor: sessionId changed in SET_STORE_RESPONSE_DATA', {
          oldSessionId: state.sessionId,
          newSessionId: restProps.sessionId,
          lastBlockId: lastBlock?.id
        });
      }

      return {
        ...state,
        sessionId: restProps.sessionId ?? state.sessionId,
        isFallback: restProps.isFallback ?? state.isFallback,
        blocks:
          state.blocks.currentBlock === null
            ? {
                ...state.blocks,
                currentBlock: buildStoredBlock(lastBlock, restProps)
              }
            : {
                lastBlock: buildStoredBlock(prevBlock),
                currentBlock: buildStoredBlock(lastBlock, restProps)
              }
      };
    },
    [DialogProcessorActions.Type.RESET_BLOCKS]: (state, action): IDProcessState => {
      logger.info('Resetting blocks in dialog processor', {
        sessionId: state.sessionId
      });
      return {
        ...state,
        blocks: { lastBlock: null, currentBlock: null },
        isFallback: false
      };
    },
    [DialogProcessorActions.Type.SET_SESSION_ID]: (state, action: PayloadAction<string>): IDProcessState => {
      if (!action.payload) {
        logger.warn('Attempted to set null sessionId via SET_SESSION_ID', {
          currentSessionId: state.sessionId
        });
        return state; // Don't update state if trying to set null sessionId
      }
      logger.info('DialogProcessor: Setting sessionId via SET_SESSION_ID', {
        oldSessionId: state.sessionId,
        newSessionId: action.payload
      });
      return { ...state, sessionId: action.payload };
    }
  },
  initialState
);
